import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { H3 } from '@osmosis/tailwind-ui/components/Text';
import React, { useMemo, useRef, useState, useCallback } from 'react';
import { NavList } from '@osmosis/shared/types/learndb/learnData';
import { NavListItem } from '@osmosis/shared/types/learndb/learnData';

import LearnSidenavItemRow from './LearnSidenavItemRow';
import useOnScreen from '../../../hooks/useOnScreen';
import { useWatchProgress } from '../../../context/WatchProgressProvider';
import { useUserContext } from '../../../context/UserContext';

import PlaylistIcon from '../../icons/PlaylistIcon';
import { useColors } from '@osmosis/tailwind-ui/helpers/useColors';
import dynamic from 'next/dynamic';

const AddToPlaylistButton = dynamic(() => {
  return import('../../../app/_components/playlist/AddToPlaylistButton');
});

type Props = {
  data: NavList;
  selectionMode: boolean;
  currentTitle?: string;
  selectedItems?: NavListItem[];
  onChangeCheckedValue: (item: NavListItem, checked: boolean) => void;
  playlistName: string;
  selectedTab: string;
  className?: string;
  index: number;
};

export type HoveredItem = Pick<NavListItem, 'learnNodeID'> & {
  title: NavListItem['name'];
  current: boolean;
};

/**
 * .
 */
const VideoList = React.memo(function VideoList(props: Props) {
  const {
    index: navIndex,
    data: { items, title },
    playlistName = '',
    selectionMode,
    currentTitle = '',
    selectedItems,
    onChangeCheckedValue,
    selectedTab,
    className = '',
  } = props;
  const [tbr, setTBR] = useState<boolean>(false);
  const { watchProgress } = useWatchProgress();
  const { user, hasPrime } = useUserContext();

  const [hoveredItem, setHoveredItem] = useState<HoveredItem | null>(null);
  const [activeItem, setActiveItem] = useState<HoveredItem | null>(null);

  const ref = useRef<HTMLDivElement>(null);
  const isOnScreen = useOnScreen(ref);
  const colors = useColors();

  useMemo(() => {
    setTBR(selectedTab.includes('To be retired'));
  }, [selectedTab]);

  const iconColor = useMemo(() => {
    if (hoveredItem?.current) {
      return colors['blueDark'];
    }
    return '';
  }, [hoveredItem]);

  const addToPlaylistIcon = React.useMemo(() => {
    return (
      user &&
      !tbr &&
      isOnScreen && (
        <AddToPlaylistButton
          items={
            activeItem
              ? [
                  {
                    name: activeItem?.title,
                    learnNodeID: activeItem?.learnNodeID,
                  },
                ]
              : []
          }
          isIconButton={true}
          iconButtonProps={{
            className:
              'mr-2 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 group-focus:opacity-100 group-active:opacity-100',
            style: {
              fontSize: 16,
            },
            variant: 'transparent',
            icon: <PlaylistIcon fill="text" color={iconColor} />,
          }}
          data-testid={`playlist-item-row-add-to-playlist-button-${title}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setActiveItem(hoveredItem);
          }}
        />
      )
    );
  }, [hoveredItem, activeItem, iconColor, isOnScreen, tbr, title, user]);

  const handleItemHover = useCallback((item: HoveredItem) => {
    setHoveredItem(item);
  }, []);
  const handleCheckboxChange = useCallback(
    (item: NavListItem, checked: boolean) => () =>
      onChangeCheckedValue(item, checked),
    [onChangeCheckedValue],
  );

  return (
    <Flex className={`flex-col ${className}`} ref={ref}>
      <H3 className={`mb-2`}>{playlistName || title}</H3>
      <div
        className={
          selectionMode
            ? ''
            : "relative after:content-[''] after:absolute after:block after:w-[1px] after:h-[calc(100%_-40px)] after:top-5 after:left-[12px] after:bg-gray-100"
        }
      >
        {items.map((item, index, call) => {
          const checked = selectedItems?.find(
            (i) => i.learnNodeID === item.learnNodeID,
          );

          return (
            <LearnSidenavItemRow
              key={index}
              learnNodeID={item.learnNodeID}
              prime={item.prime}
              selectionMode={selectionMode}
              title={item.name}
              link={item.link}
              type={item.pageType}
              progress={watchProgress?.[item.learnNodeID]?.percent}
              current={currentTitle.toLowerCase() === item.name.toLowerCase()}
              checked={!!checked}
              onChangeCheckedValue={handleCheckboxChange(item, !checked)}
              isOnScreen={navIndex === 0 || isOnScreen}
              user={user}
              hasPrime={hasPrime}
              tbr={tbr}
              handleItemHover={handleItemHover}
              addToPlaylistIcon={
                hoveredItem?.learnNodeID === item.learnNodeID
                  ? addToPlaylistIcon
                  : null
              }
            />
          );
        })}
      </div>
    </Flex>
  );
});
export default VideoList;
